/**
 * Marvin's words as the native engine sends them: preprocessed markdown whose citations are the
 * links native opens (`/doc/...` for the viewer, `/ref-file?...` for a raw file), with widgets
 * split out. Shared by the engine's markdown segments (`conversationRuntime.ts`) and its turn
 * report (`turn/engineTurn.ts`).
 */
import type { ApiFile, ApiReference } from "../shared/interfaces";
import { preprocessMessageMarkdown } from "../util/markdownStreamUtils";
import { splitMarkdownByWidgets, type RenderSegment } from "../util/widgetTokens";
import type {
    ConversationReferenceResolver,
    ConversationReferenceTarget,
    EngineSegment,
} from "./conversationRuntime";
import type { MarvinAction, MarvinQuestion } from "./messageParts";
import { ConversationReferenceProcessor as ReferenceProcessor } from "./referenceProjection";

const REFERENCE_GROUP_LINK = /\[([^\]]*)\]\(#REFERENCEGROUP\)/g;

function highlightQuery(reference: ApiReference): string | null {
    const bounds = Object.values(reference.bounds ?? {})[0];
    if (!bounds) return null;
    return `${bounds.page},${bounds.xMin},${bounds.yMin},${bounds.xMax},${bounds.yMax}`;
}

export function referenceHref(
    reference: ApiReference,
    file: ApiFile | undefined,
    description: string,
    resolver: ConversationReferenceResolver | undefined,
): string | null {
    const candidates: Array<ConversationReferenceTarget | null | undefined> = [
        reference.sectionUuid ? resolver?.getSectionTarget(reference.sectionUuid) : undefined,
        resolver?.getFileTarget(reference.fileId),
        reference.documentId
            ? {
                  documentId: reference.documentId,
                  sectionGroupId: reference.sectionGroupId,
              }
            : null,
    ];
    const resolved = candidates.find((candidate) => Boolean(candidate?.documentId));
    const highlight = highlightQuery(reference);
    if (resolved?.documentId) {
        const base =
            `/doc/${resolved.documentId}` +
            (resolved.sectionGroupId ? `/sg/${resolved.sectionGroupId}` : "");
        const params = new URLSearchParams();
        if (highlight) params.set("hl", highlight);
        if (description) params.set("title", description);
        const query = params.toString().replace(/\+/g, "%20");
        return query ? `${base}?${query}` : base;
    }
    if (!file?.fileUrl) return null;
    const params = new URLSearchParams({ fileId: reference.fileId, fileUrl: file.fileUrl });
    if (file.fileName) params.set("name", file.fileName);
    if (description) params.set("title", description);
    if (reference.sectionUuid) params.set("sectionUuid", reference.sectionUuid);
    if (highlight) params.set("hl", highlight);
    return `/ref-file?${params.toString().replace(/\+/g, "%20")}`;
}

export function inlineReferenceLinks(
    markdown: string,
    processor: ReferenceProcessor,
    referencedFiles: Map<string, ApiFile>,
    resolver: ConversationReferenceResolver | undefined,
): string {
    return markdown.replace(REFERENCE_GROUP_LINK, (_whole, keys: string) =>
        keys
            .split(",")
            .map((key) => key.trim())
            .filter((key) => processor.isReferenceValid(key))
            .map((key) => {
                const reference = processor.getReferenceOrNull(key);
                const label = processor.getText(key).replace(/\s*\|\s*/g, " · ");
                const href = reference
                    ? referenceHref(
                          reference,
                          referencedFiles.get(reference.fileId),
                          processor.getDescription(key),
                          resolver,
                      )
                    : null;
                return href ? `[${label}](${href})` : label;
            })
            .join(", "),
    );
}

/**
 * Preprocessed markdown with its citations as the links native opens, split around the widgets it
 * carries: what every native rendering of Marvin's words starts from.
 */
export function linkedRenderSegments(
    processedMarkdown: string,
    referenceMap: Record<string, ApiReference>,
    files: ApiFile[],
    resolver: ConversationReferenceResolver | undefined,
): RenderSegment[] {
    const processor = new ReferenceProcessor(new Map(Object.entries(referenceMap)), files);
    const processed = inlineReferenceLinks(
        processedMarkdown,
        processor,
        new Map(files.map((file) => [file.id, file])),
        resolver,
    );
    return splitMarkdownByWidgets(processed);
}

export function segmentsFromProcessedMarkdown(
    processedMarkdown: string,
    referenceMap: Record<string, ApiReference>,
    files: ApiFile[],
    resolver: ConversationReferenceResolver | undefined,
): EngineSegment[] {
    // A widget the server still sends this client is dropped, never shown to native as JSON.
    return linkedRenderSegments(processedMarkdown, referenceMap, files, resolver).flatMap(
        (segment) =>
            segment.kind === "markdown" && segment.content.trim().length > 0
                ? [{ kind: "markdown" as const, content: segment.content }]
                : [],
    );
}

export function normalizedSegments(
    markdown: string,
    isStreaming: boolean,
    referenceMap: Record<string, ApiReference>,
    files: ApiFile[],
    resolver: ConversationReferenceResolver | undefined,
): EngineSegment[] {
    return segmentsFromProcessedMarkdown(
        preprocessMessageMarkdown(markdown, isStreaming),
        referenceMap,
        files,
        resolver,
    );
}

/** A question card in words, for a client that draws no card for it. */
export function questionMarkdown(question: MarvinQuestion): string {
    const lines = [
        `> **${question.status === "pending" ? "Question — response required" : "Question"}**`,
        ">",
    ];
    for (const line of question.question.split("\n")) lines.push(`> ${line}`);
    for (const option of question.options ?? []) {
        const optionLines =
            `${option.label}${option.description ? ` — ${option.description}` : ""}`.split("\n");
        lines.push(`> - ${optionLines[0]}`);
        for (let i = 1; i < optionLines.length; i++) lines.push(`>   ${optionLines[i]}`);
    }
    if (question.allowOther) lines.push("> - Something else");
    if (question.status === "pending") {
        // A question ends the turn: the next message is the answer, typed here like any other.
        lines.push(">", "> _Type your answer below to reply._");
    } else {
        const answers = [...(question.answers ?? [])];
        if (question.otherText) answers.push(question.otherText);
        lines.push(">", `> _Answered: ${answers.join(", ") || "Skipped"}_`);
    }
    return lines.join("\n");
}

/** An action card in words, for a client that draws no card for it. */
export function actionMarkdown(action: MarvinAction): string {
    // An auto action (a draft Marvin made) never waits for anyone's approval.
    const awaitingApproval = action.status === "pending" && !action.autoApproved;
    const lines = [
        `> **${awaitingApproval ? "Action approval required" : "Action"}: ${action.title}**`,
    ];
    if (action.summary) {
        lines.push(">");
        for (const line of action.summary.split("\n")) lines.push(`> ${line}`);
    }
    if (awaitingApproval) {
        lines.push(
            ">",
            "> _This run is waiting for approval. Open this conversation in the web app to approve or reject it._",
        );
    } else if (action.status !== "pending") {
        lines.push(">", `> _${action.resultSummary ?? `Status: ${action.status}`}_`);
    }
    return lines.join("\n");
}
